import { useEffect, useState } from "react";
import { List } from "@refinedev/antd";
import { useOne, useUpdate } from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  Typography,
  message,
} from "antd";

import {
  TvSettings,
  TvSyncResult,
  tvErrorMessage,
  tvErrorNotification,
  tvSuccessNotification,
} from "../Lib";
import { runTvSync } from "../Providers/tv-api";
import { TvImageField } from "../Components";

export const TvSettingsPage = () => {
  const [form] = Form.useForm<TvSettings>();
  // кнопка в шапке — отдельная карточка, но сохраняется вместе с остальными настройками
  const [linkForm] = Form.useForm<TvSettings>();
  const [pageForm] = Form.useForm<TvSettings>();
  const [adForm] = Form.useForm<TvSettings>();
  const { data, refetch } = useOne<TvSettings>({
    resource: "tv-settings",
    dataProviderName: "tvDataProvider",
    id: "current",
  });
  const { mutate, isLoading } = useUpdate();
  const [sync, setSync] = useState<{ full: boolean; result: TvSyncResult }>();
  const [running, setRunning] = useState<boolean | null>(null);

  useEffect(() => {
    if (data?.data) {
      form.setFieldsValue(data.data);
      linkForm.setFieldsValue(data.data);
      pageForm.setFieldsValue(data.data);
      adForm.setFieldsValue(data.data);
    }
  }, [data, form, linkForm, pageForm, adForm]);

  const save = () =>
    mutate(
      {
        resource: "tv-settings",
        dataProviderName: "tvDataProvider",
        id: "current",
        values: {
          ...data?.data,
          ...form.getFieldsValue(),
          ...linkForm.getFieldsValue(),
          // пустая строка — визуал убрали (null бэк понимает как «не менять»)
          headerLinkImage: linkForm.getFieldValue("headerLinkImage") ?? "",
          // пустая строка — обложку убрали (null бэк понимает как «не менять»)
          originalsCover: pageForm.getFieldValue("originalsCover") ?? "",
          // пустые строки — рекламу выключили
          adfoxOwnerId: adForm.getFieldValue("adfoxOwnerId") ?? "",
          adfoxP1: adForm.getFieldValue("adfoxP1") ?? "",
          adfoxP2: adForm.getFieldValue("adfoxP2") ?? "",
          adfoxEnabled: adForm.getFieldValue("adfoxEnabled"),
        },
        ...tvSuccessNotification("Настройки сохранены"),
        ...tvErrorNotification(),
      },
      { onSuccess: () => refetch() }
    );

  const runSync = async (full: boolean) => {
    setRunning(full);
    try {
      setSync({ full, result: await runTvSync(full) });
    } catch (e) {
      const status = (e as { statusCode?: number }).statusCode;
      message.error(
        status === 409
          ? "Перенос уже идёт, повторите через минуту"
          : tvErrorMessage(e)
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <List
      title="ФНЛ ТВ — настройки"
      resource="tv-settings"
      breadcrumb={false}
      canCreate={false}
    >
      <Space
        direction="vertical"
        size={24}
        style={{ width: "100%", maxWidth: 820 }}
      >
        <Card title="Витрина">
          <Form form={form} layout="inline" onFinish={save}>
            <Form.Item
              name="showcaseMaxSlides"
              label="Слайдов на витрине"
              rules={[
                {
                  required: true,
                  type: "number",
                  min: 1,
                  max: 20,
                  message: "От 1 до 20",
                },
              ]}
            >
              <InputNumber min={1} max={20} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Сохранить
            </Button>
          </Form>
          <Typography.Paragraph
            type="secondary"
            style={{ marginTop: 12, marginBottom: 0 }}
          >
            Для вошедших пользователей сверху добавляются до 4 личных слайдов,
            слайды «только гостям» им не показываются — их место занимают
            следующие по порядку. Всего слайдов не больше этого числа.
          </Typography.Paragraph>
        </Card>

        <Card title="Кнопка спецпроекта в шапке">
          <Form form={linkForm} layout="vertical" onFinish={save}>
            <Form.Item
              name="headerLinkEnabled"
              label="Показывать в шапке"
              valuePropName="checked"
              extra="Выключено — кнопки на сайте нет, а ссылка, иконка, визуал и название сохраняются"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="headerLinkUrl"
              label="Ссылка"
              extra="Внешний сайт — https://…, откроется в новой вкладке. Страница ФНЛ ТВ — адрес с /, например /show/dvoyka. Пусто — кнопки нет"
              rules={[
                {
                  pattern: /^(https?:\/\/\S+|\/\S*)$/,
                  message: "Нужна ссылка https://… или адрес, начинающийся с /",
                },
              ]}
            >
              <Input placeholder="https://… или /show/…" allowClear />
            </Form.Item>
            <Form.Item
              name="headerLinkIcon"
              label="Иконка · квадрат или прямоугольник"
              extra="PNG или WebP с прозрачным фоном, 38 px в высоту и до 128 px в ширину (на сайте вдвое меньше: 19 px, до 64 px). Стоит слева от названия. Без иконки и без визуала кнопки в шапке нет"
            >
              <TvImageField label="Иконка" />
            </Form.Item>
            <Form.Item
              name="headerLinkImage"
              label="Визуал · прямоугольник"
              extra="PNG или WebP с прозрачным фоном, 72 px в высоту и до 260 px в ширину (на сайте вдвое меньше: 36 px, до 130 px). Если загружен — в шапке и в мобильном меню только он, без иконки и названия; название остаётся подсказкой при наведении."
            >
              <TvImageField label="Визуал" />
            </Form.Item>
            <Form.Item
              name="headerLinkTitle"
              label="Название"
              extra="Подпись рядом с иконкой и пункт мобильного меню; с визуалом — только подсказка при наведении"
              rules={[{ max: 60, message: "До 60 символов" }]}
            >
              <Input maxLength={60} allowClear />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Сохранить
            </Button>
          </Form>
        </Card>

        <Card title="Страница «ФНЛ ТВ Originals»">
          <Form form={pageForm} layout="vertical" onFinish={save}>
            <Form.Item
              name="originalsCover"
              label="Обложка шапки · 1920×640 и больше"
              extra="Фото за заголовком страницы. Пусто — фон по умолчанию (паттерн)"
            >
              <TvImageField label="Обложка" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Сохранить
            </Button>
          </Form>
        </Card>

        <Card title="Реклама · Яндекс AdFox">
          <Form form={adForm} layout="vertical" onFinish={save}>
            <Typography.Paragraph type="secondary">
              Баннер на главной и на страницах лиг перед «Хайлайтами» (место
              1240×120 в макете). Коды — из кабинета AdFox, как у баннеров
              fnl.pro. Пусто — рекламы на сайте нет.
            </Typography.Paragraph>
            <Form.Item
              name="adfoxEnabled"
              label="Показывать рекламу"
              valuePropName="checked"
              extra="Выключено — баннера на сайте нет, а коды сохраняются"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="adfoxOwnerId"
              label="Номер кабинета (ownerId)"
              extra="У fnl.pro — 9750440"
              rules={[{ pattern: /^\d{1,12}$/, message: "Только цифры" }]}
            >
              <Input allowClear placeholder="9750440" />
            </Form.Item>
            <Form.Item
              name="adfoxP1"
              label="Код размещения p1"
              rules={[
                {
                  pattern: /^[A-Za-z0-9_-]{1,32}$/,
                  message: "Латиница и цифры, до 32 символов",
                },
              ]}
            >
              <Input allowClear />
            </Form.Item>
            <Form.Item
              name="adfoxP2"
              label="Код размещения p2"
              rules={[
                {
                  pattern: /^[A-Za-z0-9_-]{1,32}$/,
                  message: "Латиница и цифры, до 32 символов",
                },
              ]}
            >
              <Input allowClear />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Сохранить
            </Button>
          </Form>
        </Card>

        <Card title="Перенос видео из медиа матчей и трансляций">
          <Typography.Paragraph>
            Обзоры и пресс-конференции из медиа матчей, записи трансляций
            сыгранных матчей и выпуски из старого раздела «Видео» появляются на
            ФНЛ ТВ сами: каждые 10 минут переносится свежее за 3 дня, ночью —
            вся история. Выпуск из старого раздела «Видео» считается свежим по
            своей дате: с датой старше 3 дней он перенесётся ночной сверкой или
            кнопкой «Сверить всю историю». Запускать вручную нужно, только чтобы
            не ждать.
          </Typography.Paragraph>
          <Space wrap>
            <Button
              onClick={() => runSync(false)}
              loading={running === false}
              disabled={running !== null}
            >
              Перенести свежее (3 дня)
            </Button>
            <Button
              onClick={() => runSync(true)}
              loading={running === true}
              disabled={running !== null}
            >
              Сверить всю историю
            </Button>
          </Space>
          {sync && (
            <Alert
              style={{ marginTop: 16 }}
              type={sync.result.failed ? "warning" : "success"}
              showIcon
              message={
                sync.full
                  ? "Сверка истории завершена"
                  : "Перенос свежего завершён"
              }
              description={
                <Descriptions size="small" column={2}>
                  <Descriptions.Item label="Новых видео">
                    {sync.result.created}
                  </Descriptions.Item>
                  <Descriptions.Item label="Обновлён плеер">
                    {sync.result.linkUpdated}
                  </Descriptions.Item>
                  <Descriptions.Item label="Скрыто (источник удалён)">
                    {sync.result.hidden}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ошибок">
                    {sync.result.failed}
                  </Descriptions.Item>
                  <Descriptions.Item label="Пропущено: не ВК">
                    {sync.result.skippedNotVk}
                  </Descriptions.Item>
                  <Descriptions.Item label="Пропущено: уже есть">
                    {sync.result.skippedDuplicate}
                  </Descriptions.Item>
                </Descriptions>
              }
            />
          )}
        </Card>
      </Space>
    </List>
  );
};
